import PropTypes from 'prop-types'
import { useEffect, useMemo, useState } from 'react'
import { useImmer } from 'use-immer'
import { ModalPasswordConfirmation, UiCard, UiCardBody, UiCardHeader, UiDataTable } from 'components/common'
import { Button, Flex, FormControl, FormLabel, IconButton, Input, Select, Text, useDisclosure } from '@chakra-ui/react'
import { getConfigByName, getPayoutProjects, getPayoutRateForSalesUsers, runPayoutPayProjects } from 'api'
import { APP_CONFIG_FIELDS, PAYOUT_FILTER_STATUSES, PROJECT_DEPOSIT_STATUS, PROJECT_FLIGHT_CHECK, PROJECT_PERMIT, PROJECT_STAGES, ROLES, getAssetUrl, numberFormatter } from 'lib'
import { format as formatDate } from 'date-fns'
import { ModalPriceBreakdown } from './ModalPriceBreakdown'
import { ModalProjectCommissionEdit } from './ModalProjectCommissionEdit'
import { MdEdit } from 'react-icons/md'
import { useAppContext } from 'hooks/useAppContext'

const COMMISSION_COLUMNS = [
  {
    label: 'Sales User',
    orderBy: 'salesUserName',
    render: ({ salesUserName }) => salesUserName,
    style: {
      paddingLeft: '12px',
      paddingRight: '12px'
    }
  },
  {
    label: 'Order Date',
    orderBy: 'createdAt',
    render: ({ createdAt }) => formatDate(new Date(createdAt), 'MM/dd/yyyy'),
    style: {
      paddingLeft: '12px',
      paddingRight: '12px'
    }
  },
  {
    label: 'Project Name',
    orderBy: 'customerCompany',
    render: ({ customerCompany }) => customerCompany,
    style: {
      paddingLeft: '12px',
      paddingRight: '12px'
    }
  },
  {
    label: 'Stage',
    orderBy: 'stage',
    render: ({ stage }) => stage,
    style: {
      paddingLeft: '12px',
      paddingRight: '12px'
    }
  },
  {
    label: 'Pre-Production Conference',
    orderBy: 'flightCheck',
    render: ({ flightCheck }) => flightCheck,
    style: {
      paddingLeft: '12px',
      paddingRight: '12px'
    }
  },
  {
    label: 'Deposit',
    orderBy: 'depositStatus',
    render: ({ depositStatus }) => depositStatus,
    style: {
      paddingLeft: '12px',
      paddingRight: '12px'
    }
  },
  {
    label: 'Permit',
    orderBy: 'permit',
    render: ({ permit }) => permit,
    style: {
      paddingLeft: '12px',
      paddingRight: '12px'
    }
  },
  {
    label: 'Gross Sale',
    orderBy: 'grossSale',
    render: ({ grossSale }) => '$ ' + numberFormatter(grossSale),
    style: {
      paddingLeft: '12px',
      paddingRight: '12px'
    }
  },
  {
    label: 'Price Breakdown',
    orderBy: null,
    render: ({ id, customerCompany, onViewPriceBreakdown }) => {
      return (
        <Button size="xs" colorScheme="blue" onClick={() => onViewPriceBreakdown(id, customerCompany)}>View</Button>
      )
    },
    style: {
      paddingLeft: '12px',
      paddingRight: '12px'
    }
  },
  {
    label: 'CQ',
    orderBy: 'cq',
    render: ({ CQ, negativeChangeOrdersAmount }) => '$ ' + numberFormatter(CQ + negativeChangeOrdersAmount),
    style: {
      paddingLeft: '12px',
      paddingRight: '12px'
    }
  },
  {
    label: 'TR',
    orderBy: null,
    render: ({ TR }) => '$ ' + numberFormatter(TR),
    style: {
      paddingLeft: '12px',
      paddingRight: '12px'
    }
  },
  {
    label: 'Rate',
    orderBy: null,
    render: ({ rate }) => rate + '%',
    style: {
      paddingLeft: '12px',
      paddingRight: '12px'
    }
  },
  {
    label: 'Pay',
    orderBy: null,
    render: ({ id, pay, onEditCommissionAmount }) => {
      return (
        <Flex>
          <Text>$ {numberFormatter(pay)}</Text>
          <IconButton
            variant="ghost"
            size="sm"
            colorScheme="gray"
            icon={<MdEdit fontSize="18px" />}
            onClick={() => onEditCommissionAmount(id, pay)}
          />
        </Flex>
      )
    },
    style: {
      paddingLeft: '12px',
      paddingRight: '12px'
    }
  },
  {
    label: 'Payment Status',
    orderBy: 'commissionPaid',
    render: ({ commissionPaid }) => commissionPaid ? 'Paid' : 'Unpaid',
    style: {
      paddingLeft: '12px',
      paddingRight: '12px'
    }
  }
]

// salespersonId required when the component is used in dashboard
export const CommissionProjects = ({ isMyCommission = false, isForDashboard = false, salespersonId = 'All' }) => {
  const { currentUser, createToast } = useAppContext()
  const [tableConfig, setTableConfig] = useImmer({
    curPage: 1,
    totalPages: 1,
    totalRecords: 0,
    itemsPerPage: 10,
    search: '',
    orderBy: {
      key: 'customerCompany',
      direction: 'asc'
    }
  })
  const [isLoading, setIsLoading] = useState(false)
  const [projects, setProjects] = useState([])
  const [filterProjects, setFilterProjects] = useImmer({
    status: PAYOUT_FILTER_STATUSES.All,
    startDate: '2000-01-01',
    endDate: formatDate(new Date(), 'yyyy-MM-dd'),
    salespersonId: isMyCommission ? currentUser?.id : 'All'
  })
  const [salesUsers, setSalesUsers] = useState([])
  const [commissionDiscountPercentage, setCommissionDiscountPercentage] = useState(0)

  const disclosurePriceBreakdown = useDisclosure()
  const [selectedProjectForPriceBreakdown, setSelectedProjectForPriceBreakdown] = useState(null)

  const disclosureCommissionEdit = useDisclosure()
  const [selectedProjectForCommissionEdit, setSelectedProjectForCommissionEdit] = useState(null)

  const disclosurePasswordConfirmation = useDisclosure()

  const [allSelected, setAllSelected] = useState(false)
  const [selectedProjects, setSelectedProjects] = useState([])

  useEffect(() => {
    const loadRateForSalesUsers = async () => {
      try {
        const response = await getPayoutRateForSalesUsers()
        setSalesUsers(response)
      } catch (err) {
        console.log(err)
      }
    }
    const loadCommissionDiscountPercentage = async () => {
      try {
        const response = await getConfigByName(APP_CONFIG_FIELDS.CommissionDiscountPercentage)
        setCommissionDiscountPercentage(response.value)
      } catch (err) {
        console.log(err)
      }
    }
    loadRateForSalesUsers()
    loadCommissionDiscountPercentage()
  }, [])

  useEffect(() => {
    if (currentUser?.id) {
      setFilterProjects(draft => {
        draft.salespersonId = isMyCommission ? currentUser?.id : 'All'
      })
    }
  }, [currentUser?.id, isMyCommission])

  const load = async () => {
    setIsLoading(true)
    try {
      const response = await getPayoutProjects(
        false,
        filterProjects.status,
        filterProjects.startDate,
        filterProjects.endDate,
        (isForDashboard && salespersonId !== 'All') ? salespersonId : filterProjects.salespersonId,
        tableConfig.curPage,
        tableConfig.itemsPerPage,
        tableConfig.search,
        tableConfig.orderBy.key,
        tableConfig.orderBy.direction
      )
      setProjects(response.rows.map(project => {
        const TR = salesUsers.filter(user => user.id === project.salespersonId)?.[0]?.salesAmount || 0
        const rate = (project.isSelfInstall ? 1 : 0) + (salesUsers.filter(user => user.id === project.salespersonId)?.[0]?.commissionRate || 3)
        let pay
        if (project.commissionManualEdited) {
          pay = project.commissionManualAmount
        } else {
          pay = (project.CQ + project.negativeChangeOrdersAmount) * rate * 0.01
          if (project.discount > 0) {
            pay -= project.discount * 0.01 * commissionDiscountPercentage
          }
          if (pay < 0) {
            pay = 0
          }
        }
        return { ...project, TR, rate, pay }
      }))
      setTableConfig(draft => {
        draft.totalPages = response.totalPages
        draft.totalRecords = response.totalRecords
      })
      setIsLoading(false)
    } catch (err) {
      console.log(err)
      setIsLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [salesUsers, commissionDiscountPercentage, filterProjects.salespersonId, tableConfig.curPage, tableConfig.itemsPerPage, tableConfig.search, tableConfig.orderBy.key, tableConfig.orderBy.direction])

  const onViewPriceBreakdown = (projectId, customerCompany) => {
    disclosurePriceBreakdown.onOpen()
    setSelectedProjectForPriceBreakdown({ projectId, customerCompany })
  }

  const onEditCommissionAmount = (projectId, pay) => {
    disclosureCommissionEdit.onOpen()
    setSelectedProjectForCommissionEdit({ projectId, pay: parseFloat(pay.toFixed(2)) })
  }

  const onSelect = (e, row) => {
    if (row) {
      setSelectedProjects([...selectedProjects, row])
    } else {
      if (allSelected) {
        setSelectedProjects([])
      }
      setAllSelected(!allSelected)
    }
  }

  const handleChange = (e) => {
    setFilterProjects(draft => {
      draft[e.target.name] = e.target.value
    })
  }

  const onClickRunReport = () => {
    load()
  }

  const onClickPayRun = () => {
    disclosurePasswordConfirmation.onOpen()
  }

  const handlePayRun = async () => {
    try {
      let selectedIds = []
      if (allSelected) {
        const response = await getPayoutProjects(false, filterProjects.status, filterProjects.startDate, filterProjects.endDate, filterProjects.salespersonId, 1, 99999999, '', 'customerCompany', 'asc')
        selectedIds = response.rows.filter(v => isQualifiedProject(v)).map(v => v.id)
      } else {
        selectedIds = selectedProjects.map(v => v.id)
      }

      if (!selectedIds.length) {
        createToast('warning', 'Warning', 'No rows selected.')
        return
      }

      const response = await runPayoutPayProjects(false, selectedIds)
      window.open(getAssetUrl(response.pdf))
      createToast('success', 'Success', 'Commission has been paid for selected projects.')

      load()
      setAllSelected(false)
      setSelectedProjects([])
    } catch (err) {
      createToast('error', 'Error', err.response.data)
    }
    disclosurePasswordConfirmation.onClose()
  }

  const isQualifiedProject = (project) => {
    return !project.commissionPaid && project.stage !== PROJECT_STAGES.Staging && project.stage !== PROJECT_STAGES.PreProduction && project.flightCheck === PROJECT_FLIGHT_CHECK.Completed &&
            (project.depositStatus === PROJECT_DEPOSIT_STATUS.POReceived || project.depositStatus === PROJECT_DEPOSIT_STATUS.PaymentReceived || project.depositStatus === PROJECT_DEPOSIT_STATUS.POAndPaymentReceived) &&
            (project.permit === PROJECT_PERMIT.NotNeeded || project.permit === PROJECT_PERMIT.ClientInstall || project.permit === PROJECT_PERMIT.Approved)
  }

  const customizedProjects = useMemo(() => {
    return projects.map(v => {
      return { ...v, onViewPriceBreakdown, onEditCommissionAmount, canSelect: isQualifiedProject(v) }
    })
  }, [projects])

  return (
    <>
      <UiCard borderRadius={0}>
        <UiCardHeader bg="gray.500" p={4}>
          <Text fontWeight="bold" color="white">Projects</Text>
        </UiCardHeader>
        <UiCardBody>
          <Flex gap={4} alignItems="flex-end" mb={8}>
            <FormControl flex={1}>
              <FormLabel>Project Status</FormLabel>
              <Select name="status" value={filterProjects.status} onChange={handleChange}>
                {Object.values(PAYOUT_FILTER_STATUSES).map(status => <option key={status} value={status}>{status}</option>)}
              </Select>
            </FormControl>
            {(!isMyCommission && !(isForDashboard && salespersonId !== 'All')) && (
              <FormControl flex={1}>
                <FormLabel>Sales User</FormLabel>
                <Select name="salespersonId" value={filterProjects.salespersonId} onChange={handleChange}>
                  <option value="All">All</option>
                  {salesUsers.map(user => <option key={user.id} value={user.id}>{user.firstname} {user.lastname}</option>)}
                </Select>
              </FormControl>
            )}
            <FormControl flex={1}>
              <FormLabel>Order Date</FormLabel>
              <Input type="date" name="startDate" value={filterProjects.startDate} onChange={handleChange} />
            </FormControl>
            <FormControl flex={1}>
              <FormLabel>Thru</FormLabel>
              <Input type="date" name="endDate" value={filterProjects.endDate} onChange={handleChange} />
            </FormControl>
            <Button size="sm" colorScheme="blue" flex={1} onClick={onClickRunReport}>Run Report</Button>
          </Flex>
          {(!isForDashboard && currentUser?.role === ROLES.Admin) && (
            <Flex justifyContent="center">
              <Button size="md" colorScheme="blue" onClick={onClickPayRun}>Add to Pay Run</Button>
            </Flex>
          )}
          <UiDataTable
            mt={4}
            tableConfig={tableConfig}
            updateTableConfig={setTableConfig}
            rows={customizedProjects}
            rowKey="id"
            columns={!isForDashboard ? COMMISSION_COLUMNS : [...COMMISSION_COLUMNS.slice(0, 4), ...COMMISSION_COLUMNS.slice(12 + 1)]}
            isLoading={isLoading}
            selected={selectedProjects}
            onSelect={onSelect}
            allSelected={allSelected}
          />
        </UiCardBody>
      </UiCard>
      {!isForDashboard && (
        <>
          <ModalPriceBreakdown
            projectId={selectedProjectForPriceBreakdown?.projectId || null}
            customerCompany={selectedProjectForPriceBreakdown?.customerCompany || ''}
            isOpen={disclosurePriceBreakdown.isOpen}
            onClose={() => {
              setSelectedProjectForPriceBreakdown(null)
              disclosurePriceBreakdown.onClose()
            }}
          />
          <ModalProjectCommissionEdit
            projectId={selectedProjectForCommissionEdit?.projectId || null}
            originalAmount={selectedProjectForCommissionEdit?.pay || 0}
            isOpen={disclosureCommissionEdit.isOpen}
            onClose={() => {
              setSelectedProjectForCommissionEdit(null)
              disclosureCommissionEdit.onClose()
            }}
            onSave={() => {
              load()
              disclosureCommissionEdit.onClose()
            }}
          />
          <ModalPasswordConfirmation
            {...disclosurePasswordConfirmation}
            heading={`Add to Pay Run - ${allSelected ? 'All qualified' : selectedProjects.length} rows selected`}
            btnConfirmLabel="Confirm"
            btnCancelLabel="Cancel"
            onConfirm={handlePayRun}
          />
        </>
      )}
    </>
  )
}

CommissionProjects.propTypes = {
  isMyCommission: PropTypes.bool,
  isForDashboard: PropTypes.bool,
  salespersonId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
}
